/**
 * PORTFOLIO ANALYTICS (Investor depth). Net worth, return, yield, performance over time,
 * diversification, and cash flow. Read-only reporting derived from the portfolio.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { StatTile as Stat } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal } from '@/components/motion';

const BAR: Record<string, string> = {
  emerald: 'bg-emerald-500',
  lime: 'bg-lime-500',
  amber: 'bg-amber-500',
  violet: 'bg-violet-500',
  sky: 'bg-sky-500',
  teal: 'bg-teal-500',
};

export default function Analytics() {
  const { data, loading, error, reload } = useResource(() => api.analytics(), []);
  const [perfTable, setPerfTable] = useState(false);

  if (loading)
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <LoadingCard lines={4} />
        <LoadingCard lines={3} />
      </div>
    );
  if (error) return <ErrorState body="We could not load analytics." onRetry={reload} />;
  if (!data) return null;

  const max = Math.max(...data.performance);
  const min = Math.min(...data.performance);
  const range = max - min || 1;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/portfolio"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Portfolio
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Analytics</h1>
        <p className="mt-0.5 text-sm text-ink-muted">How your portfolio is performing.</p>
      </header>

      <Reveal>
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
          />
          <div className="relative">
            <p className="text-sm text-white/70">Net worth</p>
            <p className="mt-1 flex items-baseline gap-2">
              <span className="text-sm font-medium text-white/70">KES</span>
              <span className="tabular text-4xl font-semibold tracking-tight">{data.netWorth}</span>
            </p>
            {/* Performance chart (A00 discipline 19: a chart a screen reader cannot read is the figure
                withheld from someone, so it has a labelled image role AND a "view as table" equivalent). */}
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-white/60">Last {data.performance.length} months</p>
              <button
                onClick={() => setPerfTable((v) => !v)}
                className="text-micro font-medium text-white/80 underline-offset-2 hover:underline"
              >
                {perfTable ? 'View as chart' : 'View as table'}
              </button>
            </div>
            {perfTable ? (
              <table className="mt-2 w-full text-sm text-white/90">
                <caption className="sr-only">Net worth by month</caption>
                <tbody>
                  {data.performance.map((v, i) => (
                    <tr key={i} className="border-b border-white/15 last:border-0">
                      <td className="py-1.5 text-white/70">Month {i + 1}</td>
                      <td className="tabular py-1.5 text-right font-medium">KES {v}k</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div
                className="mt-2 flex h-16 items-end gap-1"
                role="img"
                aria-label={`Net worth trend over ${data.performance.length} months, from ${min}k to ${max}k`}
              >
                {data.performance.map((v, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t bg-white/40"
                    style={{ height: `${20 + ((v - min) / range) * 80}%` }}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </Reveal>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total return" value={`${data.totalReturn}%`} accent />
        <Stat label="Yield" value={`${data.yield}%`} />
        <Stat label="Best" value={`${data.bestPerformer.pct}%`} sub={data.bestPerformer.name} />
        <Stat
          label="Weakest"
          value={`${data.worstPerformer.pct}%`}
          sub={data.worstPerformer.name}
        />
      </div>

      <section className="rounded-card border border-border bg-surface p-5 shadow-card">
        <h2 className="font-semibold text-ink">Diversification</h2>
        <div className="mt-3 flex h-2.5 overflow-hidden rounded-full">
          {data.diversification.map((d) => (
            <div
              key={d.label}
              className={BAR[d.tone] ?? 'bg-primary'}
              style={{ width: `${d.pct}%` }}
              title={`${d.label} ${d.pct}%`}
            />
          ))}
        </div>
        <ul className="mt-3 space-y-2 text-sm">
          {data.diversification.map((d) => (
            <li key={d.label} className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-ink-muted">
                <span className={`h-2.5 w-2.5 rounded-sm ${BAR[d.tone] ?? 'bg-primary'}`} />
                {d.label}
              </span>
              <span className="tabular font-medium text-ink">{d.pct}%</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-card border border-border bg-surface p-5 shadow-card">
        <h2 className="font-semibold text-ink">Cash flow</h2>
        <ul className="mt-3 space-y-3">
          {data.cashflow.map((c) => (
            <li key={c.month} className="flex items-center justify-between text-sm">
              <span className="font-medium text-ink">{c.month}</span>
              <span className="flex gap-4">
                <span className="tabular text-accent">+ {c.income}</span>
                <span className="tabular text-ink-muted">invested {c.invested}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
